import { Fragment, useRef, useState, useEffect, useCallback } from "react"
import deepMerge from "deepmerge"
import Lottie from "lottie-web"
import { IconSpinner } from "@lib/icons"

import type { ReactNode, HTMLAttributes } from "react"
import type {
  AnimationItem,
  SVGRendererConfig,
  CanvasRendererConfig,
  HTMLRendererConfig,
} from "lottie-web"

type LottieAnimationProps = {
  fallback?: ReactNode
  path: string
  settings?: SVGRendererConfig | CanvasRendererConfig | HTMLRendererConfig
} & HTMLAttributes<HTMLDivElement>

export function LottieAnimation(props: LottieAnimationProps) {
  const { className, path, fallback, settings, ...otherProps } = props
  const [state, setState] = useState<"idle" | "pending" | "failed" | "succeed">(
    "idle",
  )
  const animationRef = useRef<AnimationItem | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  async function readAnimation(path: string) {
    try {
      setState("pending")
      const response = await fetch(path, {
        headers: {
          "Content-Type": "application/json",
        },
        cache: "force-cache",
      })

      if (response.ok) {
        setState("succeed")
        const data = await response.json()
        return data as object
      }

      setState("failed")
      return null
    } catch {
      setState("failed")
      return null
    }
  }

  const createAnimation = useCallback(
    async function createAnimation(
      settings?: SVGRendererConfig | CanvasRendererConfig | HTMLRendererConfig,
    ) {
      const animationData = await readAnimation(path)
      if (containerRef.current && animationData) {
        if (animationRef.current) animationRef.current.destroy()

        const animation = Lottie.loadAnimation({
          animationData,
          loop: true,
          autoplay: true,
          container: containerRef.current,
          renderer: "canvas",
          rendererSettings: deepMerge(
            {
              clearCanvas: true,
              progressiveLoad: true,
              preserveAspectRatio: "xMidYMid meet",
            },
            settings ?? {},
          ),
        })

        animationRef.current = animation
      }
    },
    [path],
  )

  useEffect(() => {
    createAnimation(settings)
  }, [settings, createAnimation])

  return (
    <div className={className} ref={containerRef} {...otherProps}>
      {state === "failed" && fallback && <Fragment>{fallback}</Fragment>}
      {state === "pending" && (
        <div className="m-auto h-full flex items-center justify-center">
          <IconSpinner className="animate-spin" />
        </div>
      )}
    </div>
  )
}
